/**
 * Online-booking API client.
 *
 * Wraps the mobile-ticket endpoints so the wizard component stays focused on
 * UI logic. Every helper takes the UI locale (`hy` / `en`) and translates it
 * to the SecondApi locale (`am` / `en`) before issuing the request.
 *
 * All endpoints return the standard envelope `{ isSuccess, data, message? }`.
 * We unwrap to `data` here so callers can `await fetchIndustries(lang)` and
 * get an array directly. Errors are re-thrown so the caller can show retry UI.
 */

import { SecondApi } from "../api/secondApi";
import { getSecondApiLocale } from "./apiLocale";
import {
  formatReqDate,
  generateCancelTicketToken,
  generateDelayTicketToken,
  generateRatingToken,
  generateTicketToken,
  generateTicketsToken,
  getOrCreateAppId,
} from "./bookingClient";

function unwrap(response) {
  const payload = response?.data;
  if (payload && payload.isSuccess === false) {
    const err = new Error(payload.message || "Request failed");
    err.code = payload.error?.code;
    err.statusCode = payload.statusCode;
    err.payload = payload;
    throw err;
  }
  return payload?.data;
}

/**
 * Normalize any error thrown by an axios call into the same shape `unwrap`
 * uses for envelope errors (200 status + `isSuccess: false`).
 *
 * The backend sometimes returns business errors with a non-2xx HTTP status
 * (e.g. 400 + `{ isSuccess: false, message: "..." }`). axios rejects those
 * before `unwrap` can run, so the original `err.message` is the generic
 * "Request failed with status code 400" — useless for the UI.
 *
 * This helper inspects `err.response.data` and, when an envelope is found,
 * surfaces its `message` / `error.code` / `statusCode` on the rethrown
 * error. Network failures and non-envelope responses pass through unchanged.
 */
export function normalizeBookingError(err) {
  if (!err) return new Error("Unknown error");
  const payload = err.response?.data;
  if (payload && typeof payload === "object" && "isSuccess" in payload) {
    const validationMessages = payload.errors
      ? Object.values(payload.errors).flat().filter(Boolean)
      : [];
    const next = new Error(
      validationMessages[0] ||
        payload.message ||
        err.message ||
        "Request failed",
    );
    next.code = payload.error?.code;
    next.statusCode = payload.statusCode ?? err.response?.status;
    next.payload = payload;
    next.validationErrors = payload.errors || null;
    next.cause = err;
    return next;
  }
  // Pre-existing normalized error from `unwrap` — pass through.
  if (err.payload) return err;
  return err;
}

/* =====================================================================
 * Step 1 — Industries (organization types)
 * ===================================================================== */

export async function fetchIndustries(lang) {
  const locale = getSecondApiLocale(lang);
  const res = await SecondApi.get(`/api/${locale}/organization-type/get`);
  return unwrap(res) || [];
}

/* =====================================================================
 * Step 2 — Organizations in an industry
 *
 * Backend expects `POST` with `{ type }` in the body.
 * ===================================================================== */

export async function fetchOrganizations(lang, { industryId } = {}) {
  const locale = getSecondApiLocale(lang);
  const body = industryId ? { type: Number(industryId) } : {};
  const res = await SecondApi.post(`/api/${locale}/organizations/get`, body);
  return unwrap(res) || [];
}

/* =====================================================================
 * Step 3 — Organization detail (with branches)
 *
 * The mobile API requires `app_id` in the path so the backend can record
 * "device X visited org Y". On web we use the persisted browser UUID.
 * ===================================================================== */

export async function fetchOrganizationDetail(lang, organizationId) {
  const locale = getSecondApiLocale(lang);
  const appId = getOrCreateAppId();
  const res = await SecondApi.post(
    `/api/${locale}/organizations/show/${appId}/${organizationId}`,
  );
  return unwrap(res);
}

export async function fetchBranchDetail(lang, branchId) {
  const locale = getSecondApiLocale(lang);
  const res = await SecondApi.get(`/api/${locale}/branch/${branchId}`);
  return unwrap(res);
}

/* =====================================================================
 * Step 4 — Service tree (categories)
 *
 * Two related endpoints, both under the standard `/api/{locale}/...` root:
 *
 *   GET /api/{locale}/category/{parent}/{branch}
 *     — list categories at a level. Pass `0` for the top level, or a
 *       parent category id for drill-down. Top-level returns Category
 *       records (with a `branch_category[]` embedded array); drill-down
 *       returns BranchCategory records directly. We normalize both shapes
 *       through `getServiceMeta()` so the UI doesn't have to care.
 *
 *   GET /api/{locale}/selected-category/{category}/{branch}
 *     — fetch full metadata about a single category. Useful when we have
 *       only a category id and want display info (e.g. breadcrumb labels
 *       after a deep-link). Optional in the booking flow.
 *
 * `branch_category[0].type` (or `item.type` for drill-down items) is the
 * key for navigation:
 *   - `0` → group, has sub-services → drill again
 *   - `1` → leaf, advance to time-slot step
 * ===================================================================== */

export async function fetchCategories(
  lang,
  { branchId } = {},
) {
  const locale = getSecondApiLocale(lang);
  const res = await SecondApi.get(`/api/${locale}/categories/tree/${branchId}`);
  return unwrap(res) || [];
}

export async function fetchCategoriesByUser(lang, userId) {
  const locale = getSecondApiLocale(lang);
  const res = await SecondApi.get(`/api/${locale}/categories/by-user/${userId}`);
  return unwrap(res) || [];
}

export async function fetchSelectedCategory(
  lang,
  { branchId, categoryId } = {},
) {
  const locale = getSecondApiLocale(lang);
  const res = await SecondApi.get(
    `/api/${locale}/selected-category/${categoryId}/${branchId}`,
  );
  return unwrap(res);
}

export async function fetchBranchWorkers(lang, branchId) {
  const locale = getSecondApiLocale(lang);
  const res = await SecondApi.get(`/api/${locale}/branch_by_worker/${branchId}`);
  return unwrap(res) || [];
}

export async function fetchUsersByCategory(
  lang,
  { categoryId, branchId } = {},
) {
  const locale = getSecondApiLocale(lang);
  const res = await SecondApi.get(
    `/api/${locale}/users/by-category/${categoryId}/${branchId}`,
  );
  return unwrap(res) || [];
}

/**
 * Pull a normalized {categoryId, branchCategoryId, type, windows} record
 * from a service-tree node, regardless of which level it came from.
 *
 * Top-level items expose `branch_category[0]`; drill-down items inline
 * `category_id` and `type` on the row itself. Returning a single shape
 * keeps the rest of the UI logic concise.
 */
export function getServiceMeta(item) {
  if (!item) return null;

  // Top-level shape: Category record with an embedded branch_category[]
  if (
    Array.isArray(item.branch_category) &&
    item.branch_category.length > 0
  ) {
    const bc = item.branch_category[0];
    return {
      categoryId: item.id,
      branchCategoryId: bc?.id ?? null,
      type: bc?.type,
      windows: Array.isArray(bc?.category?.windows)
        ? bc.category.windows
        : [],
      imagePath: bc?.image_path || item.image_path || null,
    };
  }

  if (
    Array.isArray(item.branch_categories) &&
    item.branch_categories.length > 0
  ) {
    const bc = item.branch_categories[0];
    return {
      categoryId: item.category_id ?? item.id,
      branchCategoryId: bc?.id ?? item.id ?? null,
      type: bc?.type ?? item.type,
      windows: Array.isArray(bc?.category?.windows)
        ? bc.category.windows
        : Array.isArray(item.windows)
          ? item.windows
          : [],
      imagePath: bc?.image_path || item.image_path || null,
    };
  }

  // Drill-down shape: BranchCategory record returned directly
  return {
    categoryId: item.category_id ?? item.id,
    branchCategoryId: item.id,
    type: item.type,
    windows: Array.isArray(item.category?.windows)
      ? item.category.windows
      : Array.isArray(item.windows)
        ? item.windows
        : [],
    imagePath: item.image_path || null,
  };
}

/** Convenience: just the numeric id we send to backend APIs. */
export function getServiceCategoryId(item) {
  return getServiceMeta(item)?.categoryId ?? null;
}

export function getServiceUserId(item) {
  const branchCategory = Array.isArray(item?.branch_category)
    ? item.branch_category[0]
    : item?.branch_category ??
      (Array.isArray(item?.branch_categories)
        ? item.branch_categories[0]
        : item?.branch_categories) ??
      null;

  return (
    item?.user_id ??
    item?.userId ??
    item?.worker_id ??
    item?.workerId ??
    item?.mobile_user_id ??
    item?.mobileUserId ??
    item?.category?.user_id ??
    item?.category?.userId ??
    branchCategory?.user_id ??
    branchCategory?.userId ??
    branchCategory?.worker_id ??
    branchCategory?.workerId ??
    branchCategory?.mobile_user_id ??
    branchCategory?.mobileUserId ??
    branchCategory?.category?.user_id ??
    branchCategory?.category?.userId ??
    getServiceMeta(item)?.user_id ??
    getServiceMeta(item)?.userId ??
    null
  );
}

const SERVICE_PRICE_FALLBACK = 0;

function normalizePriceValue(value) {
  if (value == null || value === "") return null;
  const normalized = Number(String(value).replace(/[^\d.]/g, ""));
  return Number.isFinite(normalized) && normalized > 0 ? normalized : null;
}

export function getServicePrice(item, { fallback = SERVICE_PRICE_FALLBACK } = {}) {
  if (!item) return fallback;
  const branchCategory = Array.isArray(item.branch_category)
    ? item.branch_category[0]
    : Array.isArray(item.branch_categories)
      ? item.branch_categories[0]
      : null;
  const candidates = [
    item.price,
    item.max_price,
    item.cost,
    item.amount,
    item.category?.price,
    item.category?.max_price,
    item.category?.cost,
    branchCategory?.price,
    branchCategory?.max_price,
    branchCategory?.cost,
    branchCategory?.category?.price,
    branchCategory?.category?.max_price,
    branchCategory?.category?.cost,
  ];

  for (const candidate of candidates) {
    const price = normalizePriceValue(candidate);
    if (price != null) return price;
  }

  return fallback;
}

/* Back-compat aliases — kept so older imports keep working without
 * touching every call site. New code should call `fetchCategories`. */
export function fetchTopLevelCategories(lang, branchId) {
  return fetchCategories(lang, { branchId, parentCategoryId: 0 });
}

export function fetchSubCategories(lang, parentCategoryId, branchId) {
  return fetchCategories(lang, { branchId, parentCategoryId });
}

/* =====================================================================
 * Step 5 — Available time slots
 *
 * Returns an array of "YYYY-MM-DD HH:mm:ss" strings (server local time,
 * Yerevan +04:00 unless backend says otherwise — open question).
 * ===================================================================== */

export async function fetchTimeSuggestions(
  lang,
  { branchId, categoryId, categoryIds, date, userId } = {},
) {
  const locale = getSecondApiLocale(lang);
  const normalizedCategoryIds = (
    Array.isArray(categoryIds) && categoryIds.length > 0
      ? categoryIds
      : [categoryId]
  )
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id));
  const params = {
    ...(date ? { date } : {}),
    ...(normalizedCategoryIds.length
      ? { category_ids: normalizedCategoryIds }
      : {}),
    ...(userId ? { user_id: userId } : {}),
  };
  const res = await SecondApi.get(
    `/api/${locale}/get_mobile_order_time_suggestions/${branchId}/${categoryId}`,
    {
      params,
      skipAuth: true,
      paramsSerializer: {
        serialize: (values) => {
          const searchParams = new URLSearchParams();
          Object.entries(values || {}).forEach(([key, value]) => {
            if (Array.isArray(value)) {
              searchParams.set(key, JSON.stringify(value));
              return;
            }
            if (value != null) searchParams.set(key, value);
          });
          return searchParams.toString();
        },
      },
    },
  );
  return res?.data || { data: [] };
}

/* =====================================================================
 * Step 6 — Create the ticket
 *
 * Wire format (verified against a working backend response):
 *
 *   POST /api/mobile-ticket/create
 *   Content-Type: application/json
 *   {
 *     "branch_id": 29,
 *     "category_id": 153,
 *     "app_id": "uuid-string",
 *     "req_date": "2026-06-08 13:08",
 *     "token": "$2a$04$...",
 *     "firebase_token": "web",
 *     "lang": "am"
 *   }
 *
 * Despite Swagger advertising `multipart/form-data`, the backend's STORE
 * path expects JSON. Sending FormData causes a 500 (auth middleware
 * crashes calling `MobileUser::hasPermissionTo()` on a code path that
 * only runs for the form-data variant).
 *
 * `firebase_token` is hard-coded to "web" so the backend can distinguish
 * web bookings from mobile ones (and skip push notifications). `token` is
 * a bcrypt hash of `branch_id + category_id + app_id + req_date + SECRET`
 * — see bookingClient.js for the algorithm.
 *
 * Mobile-ticket endpoints are designed for anonymous, app_id-tracked access,
 * but authenticated users should still send their web token so the backend can
 * link the booking to the account. `includeAuthIfAvailable` keeps anonymous
 * booking clean while attaching auth headers when a token exists.
 *
 * ===================================================================== */

export async function createTicket(
  lang,
  {
    branchId,
    categoryId,
    categoryIds,
    reqDate,
    clientName,
    phone,
    email,
    clientComment,
    userId,
  } = {},
) {
  const appId = getOrCreateAppId();
  const reqDateStr = formatReqDate(reqDate);
  const normalizedCategoryIds = (
    Array.isArray(categoryIds) && categoryIds.length > 0
      ? categoryIds
      : [categoryId]
  )
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id));
  const token = generateTicketToken({
    branch_id: branchId,
    category_ids: normalizedCategoryIds,
    app_id: appId,
    req_date: reqDateStr,
  });

  const body = {
    branch_id: branchId,
    category_ids: normalizedCategoryIds,
    req_date: reqDateStr,
    token,
    app_id: appId,
    firebase_token: "web",
    client_name: clientName || undefined,
    client_email: email || undefined,
    client_phone: phone ?? "",
    client_comment: clientComment || undefined,
    user_id: userId || undefined,
  };

  let res;

  try {
    res = await SecondApi.post(`/api/mobile-ticket/create`, body, {
      skipAuth: true,
      includeAuthIfAvailable: true,
    });
  } catch (err) {
    throw normalizeBookingError(err);
  }

  const payload = res?.data;

  if (payload?.isSuccess === false) {
    const err = new Error(payload.message || "Request failed");
    err.statusCode = payload.statusCode;
    err.payload = payload;
    throw err;
  }

  const item = Array.isArray(payload?.data) ? payload.data[0] : payload?.data;

  return {
    payload,
    item,
    isSuccess: payload?.isSuccess,
    message: payload?.message || "",
    errorMessage: item?.errorMessage || null,
    value: item?.value || null,
    isBlocked: item?.active !== 0,
  };
}

/* =====================================================================
 * Tickets — active / finished / missed / cancel
 *
 * All tickets endpoints take the persisted browser `app_id` in the path
 * and a freshly-generated bcrypt `token` in the query string. The token
 * is bound to `app_id` so the backend can tell us apart from someone
 * else who guessed the UUID.
 * ===================================================================== */

function buildTicketsToken(locale, appId) {
  return generateTicketsToken({
    locale,
    app_id: appId,
  });
}

/**
 * Default empty shape — the active/finished/missed endpoints all return
 * `{ order_tickets, mobile }` and now also a `client: { name }` envelope
 * for logged-in users. We pre-populate every key so destructuring at the
 * call-site is safe regardless of whether the user is authenticated.
 */
const EMPTY_TICKETS = { order_tickets: [], mobile: [], client: null };

export async function fetchActiveTickets(lang) {
  const locale = getSecondApiLocale(lang);
  const appId = getOrCreateAppId();
  
  const token = buildTicketsToken(locale, appId);
  let res;
  try {
    res = await SecondApi.get(
      `/api/${locale}/mobile-active-tickets/${appId}?token=${encodeURIComponent(token)}`,
      { skipAuth: true, includeAuthIfAvailable: true },
    );
  } catch (err) {
    throw normalizeBookingError(err);
  }
  return { ...EMPTY_TICKETS, ...(unwrap(res) || {}) };
}

export async function fetchFinishedTickets(lang) {
  const locale = getSecondApiLocale(lang);
  const appId = getOrCreateAppId();
  const token = buildTicketsToken(locale, appId);
  let res;
  try {
    res = await SecondApi.get(
      `/api/${locale}/mobile-finished-tickets/${appId}?token=${encodeURIComponent(token)}`,
      { skipAuth: true, includeAuthIfAvailable: true },
    );
  } catch (err) {
    throw normalizeBookingError(err);
  }
  return { ...EMPTY_TICKETS, ...(unwrap(res) || {}) };
}

export async function fetchMissedTickets(lang) {
  const locale = getSecondApiLocale(lang);
  const appId = getOrCreateAppId();
  const token = buildTicketsToken(locale, appId);
  let res;
  try {
    res = await SecondApi.get(
      `/api/${locale}/mobile-missed-tickets/${appId}?token=${encodeURIComponent(token)}`,
      { skipAuth: true, includeAuthIfAvailable: true },
    );
  } catch (err) {
    throw normalizeBookingError(err);
  }
  return { ...EMPTY_TICKETS, ...(unwrap(res) || {}) };
}

export async function fetchCanceledTickets(lang) {
  const locale = getSecondApiLocale(lang);
  const appId = getOrCreateAppId();
  const token = buildTicketsToken(locale, appId);
  let res;
  try {
    res = await SecondApi.get(
      `/api/${locale}/mobile-canceled-tickets/${appId}?token=${encodeURIComponent(token)}`,
      { skipAuth: true, includeAuthIfAvailable: true },
    );
  } catch (err) {
    throw normalizeBookingError(err);
  }
  return { ...EMPTY_TICKETS, ...(unwrap(res) || {}) };
}

export async function cancelTicket(lang, orderId) {
  const locale = getSecondApiLocale(lang);
  const appId = getOrCreateAppId();
  const token = generateCancelTicketToken({
    locale,
    order_id: orderId,
    app_id: appId,
  });
  let res;
  try {
    res = await SecondApi.post(
      `/api/${locale}/cancel-ticket/${orderId}/${appId}`,
      null,
      { params: { token }, skipAuth: true, includeAuthIfAvailable: true },
    );
  } catch (err) {
    throw normalizeBookingError(err);
  }
  return unwrap(res);
}

export async function createDelayTicket(lang, { orderId, minute } = {}) {
  const locale = getSecondApiLocale(lang);
  const token = generateDelayTicketToken({
    locale,
    order_id: orderId,
    minute,
  });
  let res;
  try {
    res = await SecondApi.post(
      `/api/${locale}/delay-ticket/${orderId}/${minute}`,
      null,
      { params: { token }, skipAuth: true, includeAuthIfAvailable: true },
    );
  } catch (err) {
    throw normalizeBookingError(err);
  }
  return res?.data;
}

export async function setWorkerRating(
  lang,
  { orderTicketId, rating, comment } = {},
) {
  const locale = getSecondApiLocale(lang);
  const appId = getOrCreateAppId();
  const normalizedComment = comment == null ? null : String(comment);
  if (!rating) {
    throw new Error("Rating is required");
  }
  if (normalizedComment && normalizedComment.length > 5000) {
    throw new Error("Comment must be 5,000 characters or less");
  }
  const token = generateRatingToken({
    locale,
    app_id: appId,
    order_ticket_id: orderTicketId,
    rating,
  });
  const body = {
    app_id: appId,
    order_ticket_id: orderTicketId,
    rating,
    comment: normalizedComment,
    token,
  };
  let res;
  try {
    res = await SecondApi.post(`/api/${locale}/set-rating`, body, {
      skipAuth: true,
      includeAuthIfAvailable: true,
    });
  } catch (err) {
    throw normalizeBookingError(err);
  }
  return unwrap(res);
}

export async function fetchRatingsByWorker(
  lang,
  workerId,
  { page = 1, perPage = 25 } = {},
) {
  const locale = getSecondApiLocale(lang);
  let res;
  try {
    res = await SecondApi.get(`/api/${locale}/ratings/worker/${workerId}`, {
      params: { page, per_page: perPage },
      skipAuth: true,
      includeAuthIfAvailable: true,
    });
  } catch (err) {
    throw normalizeBookingError(err);
  }
  return unwrap(res) || { ratings: [], meta: null };
}

export async function fetchRatingsByCategory(
  lang,
  categoryId,
  { page = 1, perPage = 25 } = {},
) {
  const locale = getSecondApiLocale(lang);
  let res;
  try {
    res = await SecondApi.get(`/api/${locale}/ratings/category/${categoryId}`, {
      params: { page, per_page: perPage },
      skipAuth: true,
      includeAuthIfAvailable: true,
    });
  } catch (err) {
    throw normalizeBookingError(err);
  }
  return unwrap(res) || { ratings: [], meta: null };
}

/* =====================================================================
 * Translation helpers (for backend-translated payloads)
 *
 * The backend returns `translate.name` localized to the request's locale.
 * Some endpoints return `translates[]` with multiple languages — we expose
 * a tiny helper so callers can pick the right one regardless of shape.
 * ===================================================================== */


export function armenianToLatin(text = "") {
  const map = {
    ա: "a",
    բ: "b",
    գ: "g",
    դ: "d",
    ե: "e",
    զ: "z",
    է: "e",
    ը: "y",
    թ: "t",
    ժ: "zh",
    ի: "i",
    լ: "l",
    խ: "kh",
    ծ: "ts",
    կ: "k",
    հ: "h",
    ձ: "dz",
    ղ: "gh",
    ճ: "ch",
    մ: "m",
    յ: "y",
    ն: "n",
    շ: "sh",
    ո: "o",
    չ: "ch",
    պ: "p",
    ջ: "j",
    ռ: "r",
    ս: "s",
    վ: "v",
    տ: "t",
    ր: "r",
    ց: "ts",
    ու: "u",
    փ: "p",
    ք: "q",
    և: "ev",
    օ: "o",
    ֆ: "f",
  };

  return text
    .toLowerCase()
    .replace(/ու/g, "u")
    .split("")
    .map((char) => map[char] ?? char)
    .join("");
}
export function pickTranslation(node, lang) {
  
  if (!node) return "";
  const apiLocale = getSecondApiLocale(lang);
  if (node.translate?.name) return node.translate.name;
  if (Array.isArray(node.translates)) {
    const match = node.translates.find(
      (t) => t.language?.code === apiLocale || t.language_code === apiLocale,
    );
    if (match?.name) return match.name;
    return node.translates[0]?.name || "";
  }

  
  return node.name || "";
}

export function pickAddress(node) {
  if (!node) return "";
  if (node.translate?.address) return node.translate.address;
  if (Array.isArray(node.translates)) {
    const first = node.translates.find((t) => t.address);
    if (first) return first.address;
  }
  return node.address || "";
}

/**
 * Resolve a relative `/storage/...` path returned by the backend to an
 * absolute URL using the SecondApi base URL.
 */
export function resolveStorageUrl(path) {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  const base = (process.env.REACT_APP_SECOND_API_KEY || "").replace(/\/+$/, "");
  const rel = path.startsWith("/") ? path : `/${path}`;
  return `${base}${rel}`;
}
